import React from 'react';
import axios from 'axios';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
const weatherLabel = (code) => {
    if (code === null)
        return 'Погодные условия не указаны';
    if (code === 0)
        return 'Ясно';
    if (code === 1 || code === 2)
        return 'Переменная облачность';
    if (code === 3)
        return 'Пасмурно';
    if (code === 45 || code === 48)
        return 'Туман';
    if ([51, 53, 55, 56, 57].includes(code))
        return 'Морось';
    if ([61, 63, 65, 66, 67, 80, 81, 82].includes(code))
        return 'Дождь';
    if ([71, 73, 75, 77, 85, 86].includes(code))
        return 'Снег';
    if ([95, 96, 99].includes(code))
        return 'Гроза';
    return 'Переменная погода';
};
const numberLabel = (value, digits = 0) => value === null || !Number.isFinite(value) ? '—' : value.toFixed(digits);
const dayLabel = (date) => {
    const parsed = new Date(`${date}T12:00:00`);
    return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });
};
export default function ForecastBlock({ token, fieldId }) {
    var _a;
    const [forecast, setForecast] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    React.useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        setForecast(null);
        axios.get(`/meteo/forecast/${fieldId}`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 20000,
        }).then((response) => {
            if (active)
                setForecast(response.data);
        }).catch((requestError) => {
            var _a, _b;
            if (active)
                setError(((_b = (_a = requestError === null || requestError === void 0 ? void 0 : requestError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.detail) || 'Прогноз временно недоступен.');
        }).finally(() => {
            if (active)
                setLoading(false);
        });
        return () => { active = false; };
    }, [fieldId, token]);
    if (loading) {
        return React.createElement(Stack, { "data-onboarding-state": "loading", direction: "row", spacing: 1, alignItems: "center", sx: { p: 2 } },
            React.createElement(CircularProgress, { size: 18 }),
            React.createElement(Typography, { variant: "body2" }, "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u0430\u2026"));
    }
    if (error || !((_a = forecast === null || forecast === void 0 ? void 0 : forecast.days) === null || _a === void 0 ? void 0 : _a.length)) {
        return React.createElement(Typography, { "data-onboarding-state": error ? 'error' : 'empty', variant: "body2", color: "text.secondary", sx: { p: 2 } }, error || 'Прогноз пока недоступен.');
    }
    return (React.createElement(Box, { sx: { p: 1.5, width: '100%', minWidth: 0, overflow: 'hidden' } },
        React.createElement(Stack, { direction: "row", alignItems: "baseline", justifyContent: "space-between", spacing: 1, sx: { mb: 1.25, flexWrap: 'wrap' } },
            React.createElement(Typography, { variant: "subtitle2", fontWeight: 700 }, "\u041F\u0440\u043E\u0433\u043D\u043E\u0437 \u043D\u0430 7 \u0434\u043D\u0435\u0439 \u00B7 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0435 \u043F\u043E\u043B\u0435"),
            React.createElement(Typography, { variant: "caption", color: "text.secondary" },
                "\u0422\u0435\u043A\u0443\u0449\u0438\u0435 \u0434\u0430\u0442\u044B \u043D\u0435\u0437\u0430\u0432\u0438\u0441\u0438\u043C\u043E \u043E\u0442 \u0441\u0435\u0437\u043E\u043D\u0430 \u00B7 \u043F\u043E\u043B\u0443\u0447\u0435\u043D\u043E ",
                new Date(forecast.fetched_at).toLocaleString('ru-RU'))),
        React.createElement(Stack, { direction: "row", spacing: 1, sx: { overflowX: 'auto', pb: 1.25, alignItems: 'stretch' } }, forecast.days.map((day, index) => (React.createElement(Box, { key: day.date, sx: { flex: '1 0 145px', minWidth: 145, maxWidth: 230, p: 1.25, border: '1px solid', borderColor: index === 0 ? 'primary.main' : 'divider', borderRadius: 1.5, bgcolor: index === 0 ? '#edf6ef' : '#fff' } },
            React.createElement(Typography, { variant: "subtitle2", fontWeight: 700 },
                index === 0 ? 'Сегодня · ' : '',
                dayLabel(day.date)),
            React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { display: 'block', minHeight: 23 } }, weatherLabel(day.weather_code)),
            React.createElement(Typography, { variant: "h6", fontWeight: 700, sx: { lineHeight: 1.35, my: 0.5 } },
                numberLabel(day.temperature_max),
                "\u00B0 ",
                React.createElement(Typography, { component: "span", variant: "body2", color: "text.secondary" },
                    "/ ",
                    numberLabel(day.temperature_min),
                    "\u00B0C")),
            React.createElement(Stack, { direction: "row", spacing: 0.5, alignItems: "center" },
                React.createElement(WaterDropIcon, { sx: { fontSize: 15, color: '#4c86b8' } }),
                React.createElement(Typography, { variant: "caption" },
                    numberLabel(day.precipitation_sum, 1),
                    " \u043C\u043C \u00B7 ",
                    numberLabel(day.precipitation_probability_max),
                    "%")),
            React.createElement(Stack, { direction: "row", spacing: 0.5, alignItems: "center" },
                React.createElement(AirIcon, { sx: { fontSize: 15, color: 'text.secondary' } }),
                React.createElement(Typography, { variant: "caption" },
                    "\u0412\u0435\u0442\u0435\u0440 \u0434\u043E ",
                    numberLabel(day.wind_speed_max, 1),
                    " \u043C/\u0441")),
            React.createElement(Typography, { variant: "caption", color: "text.secondary", display: "block" },
                "\u0418\u0441\u043F\u0430\u0440\u0435\u043D\u0438\u0435 ET\u2080: ",
                numberLabel(day.et0, 1),
                " \u043C\u043C"))))),
        React.createElement(Typography, { variant: "caption", color: "text.secondary" }, "\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A: Open-Meteo \u00B7 \u0440\u0430\u0441\u0447\u0451\u0442\u043D\u0430\u044F \u043F\u043E\u0433\u043E\u0434\u0430 \u043F\u043E \u043A\u043E\u043E\u0440\u0434\u0438\u043D\u0430\u0442\u0430\u043C \u0446\u0435\u043D\u0442\u0440\u0430 \u043F\u043E\u043B\u044F. \u0412\u0435\u0440\u043E\u044F\u0442\u043D\u043E\u0441\u0442\u044C \u043E\u0441\u0430\u0434\u043A\u043E\u0432 \u2014 \u043C\u0430\u043A\u0441\u0438\u043C\u0430\u043B\u044C\u043D\u0430\u044F \u0437\u0430 \u0434\u0435\u043D\u044C.")));
}
